<script setup lang="ts">
import { computed, ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { apiPost } from '@/lib/http'
import { price } from '@/helpers/numberFormatters'

interface TeacherRef {
  user_id: number
  last_name?: string
  first_name?: string
  profile_image?: string
}
interface StudentItem {
  id: number
  name?: string
  teachers: TeacherRef[]
  smart_book?: { purchased?: boolean }
  [key: string]: unknown
}
interface GrantRequest {
  teacher: TeacherRef
  student?: { id?: number; name?: string }
  grant_id?: number
  course?: { name?: string }
  tariff?: string
  price?: number | string
}

const props = defineProps<{
  students?: StudentItem[]
}>()

const emit = defineEmits<{
  (e: 'create-student'): void
}>()

const { notify } = useNotification()

const open = ref(false)
const infoOnly = ref(false)
const request = ref<GrantRequest | null>(null)
const selectedStudent = ref<number | null>(null)
const isStudentPreselected = ref(false)
const tariffNames: Record<string, string> = { b: 'стандартная', e: 'расширенная' }

const availableStudents = computed(() => {
  if (!request.value) return []
  const teacherId = request.value.teacher.user_id

  let students = (props.students ?? []).filter(student => {
    if (student.smart_book?.purchased) return false
    return student.teachers.every(e => teacherId !== e.user_id)
  })

  if (isStudentPreselected.value) {
    const selectedStudentIndex = students.findIndex(s => s.id === selectedStudent.value)

    const [selected] = students.splice(selectedStudentIndex, 1)
    students = [selected, ...students]
  }

  return students
})

function $open(infoOnlyValue: boolean, requestValue: GrantRequest, selectedStudentId?: number) {
  open.value = true
  infoOnly.value = infoOnlyValue
  request.value = requestValue
  selectedStudent.value = selectedStudentId ?? null
  isStudentPreselected.value = selectedStudentId !== undefined
}

function createStudent() {
  open.value = false
  emit('create-student')
}

async function dismissInfo(goToCabinet?: boolean) {
  try {
    const { failed, message } = await apiPost('/teacher/request_parent_confirm_smartbook_grant', {
      grant_id: request.value?.grant_id
    })

    if (failed) throw message

    if (goToCabinet) window.location.href = `/student/cabinet/${request.value?.student?.id}`
    else window.location.reload()
  } catch (err) {
    console.error(err)

    let title = 'Произошла неизвестная ошибка при сохранении данных, повторите попытку.'
    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
  }
}

async function doBindGrant() {
  try {
    const { failed, message } = await apiPost('/teacher/request_parent_confirm_smartbook_grant', {
      grant_id: request.value?.grant_id,
      student_id: selectedStudent.value
    })

    if (failed) throw message
    await dismissInfo()
  } catch (err) {
    console.error(err)

    let title = 'Произошла ошибка при попытке принять учебник. Повторите попытку позже.'
    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
  }
}

function formatPrice(val: number | string) {
  return price.rub(typeof val === 'string' ? Number(val) : val)
}

defineExpose({
  $open
})
</script>

<template>
  <v-rich-modal v-model="open" title="Вам стал доступен Умный Учебник">
    <div v-if="request" class="smartbook-granted-modal">
      <p class="mb-3 d-flex justify-space-between">
        {{ request.course?.name ?? '' }}
        ({{ tariffNames[request.tariff ?? ''] ?? '' }} версия) —
        {{ formatPrice(request.price ?? 0) }}

        <span class="smartbook-granted-modal__paid-badge">
          <v-icon class="mr-half" size="16">mdi-check</v-icon>
          Оплачено
        </span>
      </p>

      <div class="smartbook-granted-modal__header mb-2">
        <img :src="request.teacher.profile_image" />
        <div class="ma-1" />

        <p v-if="infoOnly">
          Учитель
          <strong class="text-nowrap">{{ request.teacher.last_name }} {{ request.teacher.first_name }}</strong>
          выдал доступ к учебнику для
          <strong class="text-nowrap">{{ request.student?.name ?? '' }}</strong>
        </p>
        <p v-else>
          Учитель
          <strong>{{ request.teacher.last_name }} {{ request.teacher.first_name }}</strong>
          выдал доступ к учебнику
        </p>
      </div>

      <template v-if="!infoOnly">
        <p class="text-body-bold my-1">Выберите, кому из ваших детей будет выдан доступ к Умному Учебнику:</p>

        <ul class="smartbook-granted-modal__children-container">
          <li
            v-for="student in availableStudents"
            :key="student.id"
            class="smartbook-granted-modal__child py-2 px-3"
            :class="{ 'smartbook-granted-modal__child_active': selectedStudent === student.id }"
            @click="() => (selectedStudent = student.id)"
          >
            <v-icon v-if="selectedStudent === student.id" class="blue-text"> mdi-radiobox-marked </v-icon>
            <v-icon v-else class="gray-text"> mdi-radiobox-blank </v-icon>

            <span class="flex-spacer ml-2">{{ student.name }}</span>
          </li>
        </ul>

        <v-link mode="primary block" class="my-1" @click="createStudent">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Добавить нового ребёнка
        </v-link>
      </template>
    </div>

    <template v-if="infoOnly" #actions>
      <v-button mode="outlined block" @click="dismissInfo(false)"> Закрыть </v-button>
      <div class="ma-1" />
      <v-button mode="primary filled block" @click="dismissInfo(true)"> В кабинет ученика </v-button>
    </template>
    <template v-else #actions>
      <div class="smartbook-granted-modal__footer">
        <!-- FIXME: at some point this will be shown -->
        <p v-if="false" class="mb-2 text-caption">
          Обратите внимание: после подтверждения учитель сможет видеть активность вашего ребёнка на сайте.
        </p>

        <div class="smartbook-granted-modal__actions">
          <v-button mode="primary filled block large" :disabled="!selectedStudent" @click="doBindGrant">
            Подтвердить и получить доступ
          </v-button>
          <div class="ma-1" />
          <v-button mode="outlined block large" @click="open = false"> Напомнить позже </v-button>
        </div>
      </div>
    </template>
  </v-rich-modal>
</template>

<style lang="scss">
.smartbook-granted-modal {
  display: flex;
  flex-direction: column;

  @include desktop {
    width: 550px;
  }

  @include mobile {
    height: 100%;
  }

  &__paid-badge {
    background: $green;
    color: white;
    display: flex;
    align-items: center;
    @include text-button;
    border-radius: 4px;
    padding: 4px 8px;
  }

  &__child {
    display: flex;
    border: 1px solid $gray;
    border-radius: 8px;

    margin: 16px 0;
    font-weight: bold;
    cursor: pointer;

    &_active {
      border-color: $blue;
    }
  }

  &__footer {
    @include desktop {
      width: 615px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;

    & > img {
      width: 64px;
      height: 64px;
      border-radius: 999px;
    }
  }
}
</style>
